import { useState, useEffect, useCallback, useRef } from 'react'
import {
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline'
import type { AIFeedbackItem } from './index'

interface AIAnalysisPanelProps {
  feedback: AIFeedbackItem[]
  isVisible: boolean
  onClose: () => void
  onLineHighlight?: (lineNumber: number) => void
  className?: string
}

export function AIAnalysisPanel({
  feedback,
  isVisible,
  onClose,
  onLineHighlight,
  className = ''
}: AIAnalysisPanelProps) {
  const [currentIssueIndex, setCurrentIssueIndex] = useState(0)
  const [activeFilter, setActiveFilter] = useState<'all' | 'focus' | 'refinement' | 'exploration' | 'discovery' | 'critical' | 'warning' | 'info'>('all')
  const [isCollapsed, setIsCollapsed] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  // Filter feedback based on active filter - support both new type and legacy severity
  const filteredFeedback = feedback.filter(item => {
    if (activeFilter === 'all') return true
    // New creative companion types
    if (['focus', 'refinement', 'exploration', 'discovery'].includes(activeFilter)) {
      return item.type === activeFilter
    }
    // Legacy severity support
    return item.severity === activeFilter
  })

  // Reset current index when filter changes
  useEffect(() => {
    setCurrentIssueIndex(0)
  }, [activeFilter])

  // Navigate to next/previous issue
  const navigateToIssue = useCallback((direction: 'next' | 'prev') => {
    if (filteredFeedback.length === 0) return

    let newIndex = currentIssueIndex
    if (direction === 'next') {
      newIndex = (currentIssueIndex + 1) % filteredFeedback.length
    } else {
      newIndex = (currentIssueIndex - 1 + filteredFeedback.length) % filteredFeedback.length
    }

    setCurrentIssueIndex(newIndex)

    // Highlight the line in the editor
    if (onLineHighlight && filteredFeedback[newIndex]) {
      onLineHighlight(filteredFeedback[newIndex].line)
    }
  }, [currentIssueIndex, filteredFeedback, onLineHighlight])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isVisible || isCollapsed) return

      if (e.key === 'ArrowUp' && e.altKey) {
        e.preventDefault()
        navigateToIssue('prev')
      } else if (e.key === 'ArrowDown' && e.altKey) {
        e.preventDefault()
        navigateToIssue('next')
      } else if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isVisible, isCollapsed, navigateToIssue, onClose])

  // Auto-highlight first issue when feedback changes
  useEffect(() => {
    if (filteredFeedback.length > 0 && onLineHighlight) {
      onLineHighlight(filteredFeedback[0].line)
    }
  }, [filteredFeedback, onLineHighlight])


  if (!isVisible) return null

  // Show empty state when panel is open but no feedback yet
  const hasNoFeedback = feedback.length === 0
  const currentInsight = hasNoFeedback ? null : filteredFeedback[currentIssueIndex]
  const focusCount = feedback.filter(f => f.type === 'focus').length
  const refinementCount = feedback.filter(f => f.type === 'refinement').length
  const explorationCount = feedback.filter(f => f.type === 'exploration').length
  const discoveryCount = feedback.filter(f => f.type === 'discovery').length

  // Legacy support - calculate old-style counts for backward compatibility (if needed)
  // const criticalCount = feedback.filter(f => f.severity === 'critical').length
  // const warningCount = feedback.filter(f => f.severity === 'warning').length
  // const infoCount = feedback.filter(f => f.severity === 'info').length

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'focus':
        return <span className="text-amber-500">🎯</span>
      case 'refinement':
        return <span className="text-blue-500">✨</span>
      case 'exploration':
        return <span className="text-green-500">🌱</span>
      case 'discovery':
        return <span className="text-purple-500">💡</span>
      // Legacy severity support
      case 'critical':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
      case 'warning':
        return <ExclamationCircleIcon className="w-4 h-4 text-yellow-500" />
      default:
        return <InformationCircleIcon className="w-4 h-4 text-blue-500" />
    }
  }

  const getInsightColor = (type: string) => {
    // Use dark backgrounds with proper contrast for white/gray text
    switch (type) {
      case 'focus':
        return 'border-amber-500 bg-amber-950/80'
      case 'refinement':
        return 'border-blue-500 bg-blue-950/80'
      case 'exploration':
        return 'border-green-500 bg-green-950/80'
      case 'discovery':
        return 'border-purple-500 bg-purple-950/80'
      // Legacy severity support
      case 'critical':
        return 'border-red-500 bg-red-950/80'
      case 'warning':
        return 'border-yellow-500 bg-yellow-950/80'
      default:
        return 'border-blue-500 bg-blue-950/80'
    }
  }

  return (
    <div
      ref={panelRef}
      className={`fixed right-6 top-20 bottom-6 w-96 bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-2xl shadow-2xl z-[60] transition-all duration-300 flex flex-col ${isCollapsed ? 'h-16' : ''} ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-semibold text-white">AI Analysis</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-300">
            <span className="bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded border border-amber-500/30" title="Focus Areas">🎯 {focusCount}</span>
            <span className="bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded border border-blue-500/30" title="Refinements">✨ {refinementCount}</span>
            <span className="bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded border border-green-500/30" title="Explorations">🌱 {explorationCount}</span>
            <span className="bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded border border-purple-500/30" title="Discoveries">💡 {discoveryCount}</span>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? <ChevronUpIcon className="w-4 h-4 text-gray-400" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            title="Close analysis"
          >
            <XMarkIcon className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <>
          {/* Filters */}
          <div className="flex items-center gap-1 p-3 border-b border-slate-700 flex-shrink-0">
            <FunnelIcon className="w-4 h-4 text-gray-500" />
            <div className="flex items-center gap-1">
              {[
                { value: 'all', label: 'All' },
                { value: 'focus', label: '🎯 Focus' },
                { value: 'refinement', label: '✨ Refine' },
                { value: 'exploration', label: '🌱 Explore' },
                { value: 'discovery', label: '💡 Discover' }
              ].map(filter => (
                <button
                  key={filter.value}
                  onClick={() => setActiveFilter(filter.value as 'all' | 'focus' | 'refinement' | 'exploration' | 'discovery')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    activeFilter === filter.value
                      ? 'bg-blue-500/20 text-blue-400 font-medium border border-blue-500/30'
                      : 'text-gray-400 hover:bg-white/10'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {hasNoFeedback ? (
              <div className="p-6 text-center text-gray-400">
                <div className="text-4xl mb-4 opacity-60">🤖</div>
                <p className="text-sm font-medium text-gray-300 mb-2">AI Analysis Ready</p>
                <p className="text-xs text-gray-500 mb-4">
                  Analysis runs when you request it from specific features.
                </p>
                <div className="text-xs text-gray-600 space-y-1">
                  <p>• Word Collection insights</p>
                  <p>• Rhyme suggestions</p>
                  <p>• Pattern analysis</p>
                </div>
              </div>
            ) : filteredFeedback.length === 0 ? (
              <div className="p-6 text-center text-gray-400">
                <InformationCircleIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No {activeFilter === 'all' ? '' : activeFilter} insights found</p>
              </div>
            ) : currentInsight && (
              <div className="p-4">
                {/* Current Insight */}
                <div className={`border-l-4 rounded-lg p-4 ${getInsightColor(currentInsight.type || currentInsight.severity)}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getInsightIcon(currentInsight.type || currentInsight.severity)}
                      <span className="font-medium text-white">{currentInsight.title || currentInsight.issue}</span>
                    </div>
                    <span className="text-xs text-gray-300 bg-slate-800 px-2 py-1 rounded border border-slate-600">
                      {currentInsight.location || `Line ${currentInsight.line}`}
                    </span>
                  </div>

                  <div className="text-xs text-gray-400 mb-2">
                    {currentInsight.category}
                  </div>

                  <p className="text-sm text-gray-300 mb-3">
                    {currentInsight.description}
                  </p>

                  {((currentInsight.pathways && currentInsight.pathways.length > 0) || (currentInsight.suggestions && currentInsight.suggestions.length > 0)) && (
                    <div>
                      <p className="text-xs font-medium text-gray-400 mb-2">
                        {currentInsight.pathways ? 'Pathways to explore:' : 'Suggestions:'}
                      </p>
                      <div className="space-y-1">
                        {(currentInsight.pathways || currentInsight.suggestions || []).map((item: string, i: number) => (
                          <div key={i} className="text-xs bg-white/10 text-gray-300 px-2 py-1 rounded border border-white/20">
                            → {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <button
                    onClick={() => navigateToIssue('prev')}
                    disabled={filteredFeedback.length <= 1}
                    className="flex items-center gap-1 px-3 py-1 text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronUpIcon className="w-4 h-4" />
                    Previous
                  </button>

                  <div className="text-xs text-gray-400">
                    {currentIssueIndex + 1} of {filteredFeedback.length}
                  </div>

                  <button
                    onClick={() => navigateToIssue('next')}
                    disabled={filteredFeedback.length <= 1}
                    className="flex items-center gap-1 px-3 py-1 text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronDownIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Keyboard Hints */}
                <div className="text-xs text-gray-500 text-center mt-2">
                  Use Alt+↑↓ to navigate • Esc to close
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// Export with legacy name for backward compatibility
export const AIFeedbackOverlay = AIAnalysisPanel
